/**
 * Cart VIFT Connect Sync
 *
 * Keeps the hidden `_CLP2` line-item property (VIFT_CONNECT_PROPERTY, see
 * assets/utilities.js) in sync on every merchandise line in the cart. The
 * value is "enableViftConnect" or "" (empty), identical on every line
 *
 *   - PPC/UFO customers, and customers with the user_types.is_test_customer
 *     metafield set true: always "" — VIFT Connect is disabled
 *   - Everyone else: "enableViftConnect" only when BOTH hold:
 *       - the customer's active portal (portal-manager.js) has its
 *         vift_connect_active field set true, AND
 *       - no merchandise line in the cart carries a variant with
 *         customMA.itransactDisabled set true.
 *     Otherwise "".
 *
 * The portal's vift_connect_active field is resolved via
 * window.PortalManager.getPortalData() (assets/portal-manager.js), which
 * already handles both the logged-in (customer metafield) and anonymous/UFO
 * cookie-based paths, cached in localStorage. Whether any cart line has
 * itransactDisabled can't be read from the AJAX Cart API (it carries no
 * metafields), so it's rendered server-side into the
 * `data-cart-has-itransact-disabled` attribute on <cart-items-component>
 * (snippets/cart-vift-connect-flag.liquid), which is refreshed whenever the
 * cart drawer/page sections re-render.
 *
 * Runs once on load and again on every `cart:update`. A correction (any line
 * whose _CLP2 doesn't match the desired value) is applied one line at a time
 * via /cart/change.js — a property write changes the line's key, so each
 * write re-picks its target from the freshest cart response, same as
 * cart-hkg-district.js's #writeLineStamps. Writes are serialized through a
 * shared queue so overlapping triggers (e.g. an add-to-cart immediately
 * followed by the portal resolving) never race each other's line keys.
 */

import { fetchConfig, VIFT_CONNECT_PROPERTY } from "@theme/utilities";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { isBcrsFeeLine, visibleItemCount } from "@theme/bcrs-fee";

/** `sourceId` of the re-render this module dispatches, so its own listener skips it. */
const STAMP_SOURCE_ID = "cart-vift-connect-sync";

/** Value stamped when VIFT Connect is enabled; disabled is the empty string. */
const ENABLED_VALUE = "enableViftConnect";

/** Serializes writes so overlapping triggers never race the same line keys. */
let syncQueue = Promise.resolve();

/**
 * @param {boolean} desired
 * @returns {string}
 */
function viftConnectValue(desired) {
  return desired ? ENABLED_VALUE : "";
}

/**
 * @param {any} item A cart line item.
 * @param {boolean} desired
 * @returns {boolean}
 */
function lineNeedsStamp(item, desired) {
  if (isBcrsFeeLine(item)) return false;
  const properties = item.properties ?? {};
  // A missing key is NOT equivalent to an empty-string value here — every
  // non-fee line must carry an explicit _CLP2 (even "") once this module has
  // run, so consumers can rely on the key always being present.
  if (!Object.prototype.hasOwnProperty.call(properties, VIFT_CONNECT_PROPERTY)) return true;
  return properties[VIFT_CONNECT_PROPERTY] !== viftConnectValue(desired);
}

/** Whether the current customer is PPC/UFO, from the ma:customer meta tag. */
function isPpcCustomer() {
  return document.querySelector('meta[name="ma:customer"]')?.getAttribute("data-is-ppc") === "true";
}

/** Whether the current customer has user_types.is_test_customer set, from the ma:customer meta tag. */
function isTestCustomer() {
  return document.querySelector('meta[name="ma:customer"]')?.getAttribute("data-is-test-customer") === "true";
}

/**
 * Resolves the desired _CLP2 value for the current cart/customer/portal state.
 * @returns {Promise<boolean>}
 */
async function resolveDesiredValue() {
  if (isPpcCustomer() || isTestCustomer()) return false;

  /** @type {any} */
  let portalData = null;
  try {
    portalData = (await window.PortalManager?.getPortalData?.()) ?? null;
  } catch {
    portalData = null;
  }
  if (portalData?.vift_connect_active !== "true" && portalData?.vift_connect_active !== true) {
    return false;
  }

  // Checked across every <cart-items-component> on the page (drawer + cart page can
  // both be present) rather than just the first match — one of them may not have
  // re-rendered as recently as the other (e.g. a third-party add-to-cart that only
  // requested `sections` for one of the two), and a stale "false" must never win.
  const anyItransactDisabled = Array.from(document.querySelectorAll("cart-items-component")).some(
    (el) => el.getAttribute("data-cart-has-itransact-disabled") === "true"
  );
  return !anyItransactDisabled;
}

/** Section ids of every cart surface on the page, so a correction re-renders them in the same round trip. */
function collectCartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll("cart-items-component")) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

/**
 * Applies `desired` to every merchandise line that doesn't already carry it,
 * one /cart/change.js call at a time (a property write changes the line key).
 * @param {boolean} desired
 */
async function reconcile(desired) {
  let cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: "application/json" } })
    .then(r => (r.ok ? r.json() : null))
    .catch(() => null);
  if (!cart || !Array.isArray(cart.items)) return;

  const sectionIds = collectCartSectionIds();
  let changed = false;

  for (
    let attempts = cart.items.filter((/** @type {any} */ it) => lineNeedsStamp(it, desired)).length;
    attempts > 0;
    attempts--
  ) {
    const item = cart.items.find((/** @type {any} */ it) => lineNeedsStamp(it, desired));
    if (!item) break;

    const properties = { ...item.properties, [VIFT_CONNECT_PROPERTY]: viftConnectValue(desired) };
    const remaining = cart.items.filter((/** @type {any} */ it) => lineNeedsStamp(it, desired)).length;

    /** @type {Record<string, unknown>} */
    const body = { id: item.key, quantity: item.quantity, properties };
    if (remaining === 1 && sectionIds.length) {
      body.sections = sectionIds.join(",");
      body.sections_url = window.location.pathname;
    }

    const updated = await fetch(Theme.routes.cart_change_url, fetchConfig("json", { body: JSON.stringify(body) }))
      .then(r => r.json())
      .catch(() => null);
    if (!updated || updated.status || !Array.isArray(updated.items)) {
      console.error(
        "[cart-vift-connect-sync] Failed to update line property:",
        updated?.description ?? "unknown error"
      );
      break;
    }
    cart = updated;
    changed = true;
  }

  if (!changed) return;

  const autoOpenDrawers = Array.from(document.querySelectorAll("cart-drawer-component[auto-open]"));
  for (const drawer of autoOpenDrawers) drawer.removeAttribute("auto-open");

  document.dispatchEvent(
    new CartUpdateEvent(cart, STAMP_SOURCE_ID, {
      source: STAMP_SOURCE_ID,
      itemCount: visibleItemCount(cart),
      sections: cart.sections ?? undefined,
    })
  );

  setTimeout(() => {
    for (const drawer of autoOpenDrawers) drawer.setAttribute("auto-open", "");
  }, 0);
}

/**
 * @param {(Event & { detail?: { sourceId?: string } }) | undefined} [event]
 */
function sync(event) {
  if (event?.detail?.sourceId === STAMP_SOURCE_ID) return;

  syncQueue = syncQueue
    .then(resolveDesiredValue)
    .then(reconcile)
    .catch(err => console.error("[cart-vift-connect-sync] Sync failed:", err));
}

document.addEventListener(ThemeEvents.cartUpdate, sync);
sync();
